/**
 * RoiResultsView — the read-only results side. Every number here comes straight from a RoiResults
 * computed by the engine; this component only formats and lays them out. Nullable engine outputs
 * (payback, cash-on-cash, IRR) render an honest "n/a", never a fabricated 0.
 */
import type { RoiResults } from '../../lib/roi/roiModel'
import type { ChargerType } from '../../lib/roi/presets'
import { usd, perKwh, years, percent } from './format'
import { benchmarkHint } from './benchmarkHints'

export interface RoiResultsViewProps {
  results: RoiResults
  chargerType: ChargerType
}

function Metric({
  label,
  value,
  testId,
  hint,
}: {
  label: string
  value: string
  testId?: string
  hint?: string | null
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
      <div className="text-[0.68rem] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div data-testid={testId} className="text-lg font-semibold tabular-nums text-foreground">
        {value}
      </div>
      {hint && <div className="text-[0.66rem] text-muted-foreground">Typical: {hint}</div>}
    </div>
  )
}

export function RoiResultsView({ results, chargerType }: RoiResultsViewProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
        <Metric label="Net operating income / yr" value={usd(results.noi)} testId="roi-noi" />
        <Metric label="Effective cost / kWh" value={perKwh(results.effectiveCostPerKwh)} />
        <Metric
          label="Simple payback"
          value={years(results.simplePaybackYears)}
          testId="roi-payback"
          hint={benchmarkHint('Typical simple payback', chargerType)}
        />
        <Metric label="Break-even price / kWh" value={perKwh(results.breakEvenPricePerKwh)} />
        <Metric label="Gross margin / kWh" value={perKwh(results.grossMarginPerKwh)} />
        <Metric label="Year-1 cash-on-cash" value={percent(results.cashOnCashReturn)} />
      </div>

      <div>
        <div className="mb-1 flex items-baseline justify-between">
          <h4 className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
            10-year cash flow
          </h4>
          <div className="flex gap-4 text-xs">
            <span>
              NPV{' '}
              <span data-testid="roi-npv" className="font-semibold tabular-nums text-foreground">
                {usd(results.npv)}
              </span>
            </span>
            <span>
              IRR{' '}
              <span data-testid="roi-irr" className="font-semibold tabular-nums text-foreground">
                {percent(results.irr)}
              </span>
            </span>
          </div>
        </div>
        {/* T-497 (Noreen: "so we don't need to scroll to see year 10"): the table models 11 rows
            (Year 0–10). A fixed-height `max-h-52 overflow-y-auto` box here put Year 10 below the fold
            of an inner scrollbar. Let the table render its full height instead — the dialog
            (max-h-[90vh] overflow-y-auto) still owns overflow on a short viewport. */}
        <div className="rounded-lg border border-border">
          <table data-testid="roi-cashflow" className="w-full text-right text-xs tabular-nums">
            <thead className="sticky top-0 bg-muted/60 text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Year</th>
                <th className="px-2 py-1 font-medium">Net</th>
                <th className="px-2 py-1 font-medium">Cumulative</th>
                <th className="px-2 py-1 font-medium">Discounted</th>
              </tr>
            </thead>
            <tbody>
              {results.cashFlow.map((cf) => (
                <tr key={cf.year} className="border-t border-border/50">
                  <td className="px-2 py-0.5 text-left text-muted-foreground">Year {cf.year}</td>
                  <td className="px-2 py-0.5">{usd(cf.netCashFlow)}</td>
                  <td className="px-2 py-0.5">{usd(cf.cumulativeCashFlow)}</td>
                  <td className="px-2 py-0.5">{usd(cf.discountedCashFlow)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
